import React from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import {EventTypeData} from "types/EventType";
import EventTypeForm from "pages/EventType/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

function MutateEventType() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: eventType, isLoading} = useQuery(
    ['event-types', {id}],
    () => ngage.eventTypes.get(parseInt(id as string)),
    {enabled: !!id && auth.can('event-types:read')}
  )

  const store = useMutation((data: EventTypeData) => ngage.eventTypes.store(data))
  const update = useMutation((data: EventTypeData) => ngage.eventTypes.update(parseInt(id as string), data))

  // Handle form submission
  async function handleSubmit(values: EventTypeData, {setSubmitting}: FormikHelpers<EventTypeData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('event-types.all')

    setSubmitting(false)
    navigate('/event-types')
  }

  // Handle store mutation
  async function handleStore(values: EventTypeData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Event Type')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Event Type')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: EventTypeData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Event Type')} Updated`, `${values.name} has been updated`)
      await invalidate(['event-types', {id}])
    } catch (error) {
      notify("error", `${termFor('Event Type')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('event-types:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    [termFor('Events')]: '/',
    [termFor('Event Types')]: '/event-types',
    [`New ${termFor('Event Type')}`]: '/event-types/new'
  }
  const editBreadcrumbs = {
    [termFor('Events')]: '/',
    [termFor('Event Types')]: '/event-types',
    [`Edit ${termFor('Event Type')}`]: `/event-types/${id}/edit`
  }

  return (
    <Page
      title={id ? `Edit ${termFor('Event Type')}` : `New ${termFor('Event Type')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <EventTypeForm eventType={eventType} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateEventType;